import { FooterMenuGroup } from '@v2/shared/Layouts/Full/Footer/FooterMenu';
import { openConsentPreferences, useConsentLawActive } from '@v2/shared/lib/cookieYes';

/**
 * MagSpot's footer, as data — the two columns of
 * `domain-overrides/magspot.com/layouts/full/footer.html`, in its order and with its
 * wording.
 *
 * Its own module rather than markup inside Footer because the columns themselves are
 * shared with MagZoom and only the list is MagSpot's. Everything that reads it goes
 * through `buildUrl` on the way out, so the paths are relative here.
 *
 * A hook rather than a constant, as TopMags' equivalent already is, because one entry is
 * conditional: a brand decides its own links, and the shared FooterMenu only draws what
 * it is handed.
 *
 * The legacy list carries a Font Awesome glyph beside each entry. They are decorative —
 * an envelope beside Contact — and the footer they came from is the only place they ever
 * rendered, so they do not travel with the list.
 *
 * `Delivery Policy` genuinely points into the FAQ rather than a page of its own, as it
 * does in the template.
 */
export default function useFooterMenu(): FooterMenuGroup[] {
  const consentLawActive = useConsentLawActive();

  return [
    {
      title: 'About',
      items: [
        { label: 'About Us', href: '/about-us' },
        { label: 'FAQ', href: '/help' },
        { label: 'Contact', href: '/contact' },
      ],
    },
    {
      title: 'Legal',
      items: [
        { label: 'Terms of Use', href: '/terms' },
        { label: 'Privacy Policy', href: '/privacy' },
        // Only where CookieYes says a privacy law applies — see useConsentLawActive. Cal.
        // Civ. Code 1798.135(a)(1) names the link, so the title is not ours to shorten to
        // fit the column. The click opens the preference centre; the href is where it
        // lands if CookieYes cannot be asked.
        ...(consentLawActive
          ? [
              {
                label: 'Do Not Sell or Share My Personal Information',
                href: '/privacy#your-choices',
                onClick: openConsentPreferences,
              },
            ]
          : []),
        { label: 'Accessibility Policy', href: '/accessibility' },
        { label: 'Refund & Return Policy', href: '/refund-policy' },
        { label: 'Delivery Policy', href: '/help#first-issue' },
      ],
    },
  ];
}
